import { useQuery } from "@tanstack/react-query";
import {
  getDividends,
  type DividendCalendarResponse,
} from "../../lib/api-modules/portfolio.js";
import { Card, Pill, StatCard } from "../design-system/index.js";
import { formatCentsCurrency } from "../../lib/util/money.js";

/**
 * Phase 11 — Dividend calendar dashboard tile. 30-day-forward ex-div
 * preview + projected $/yr and $/mo income.
 */
export function DividendCalendarTile() {
  const { data, isLoading, error } = useQuery<DividendCalendarResponse>({
    queryKey: ["portfolio", "dividends", 30],
    queryFn: () => getDividends(30),
  });

  return (
    <Card>
      <h3 className="mb-3 font-display text-lg font-medium text-[var(--color-text)]">
        Dividend calendar
      </h3>
      {isLoading && <p className="text-sm text-[var(--color-muted)]">Loading…</p>}
      {error && (
        <p className="text-sm text-[var(--color-danger)]">
          {error instanceof Error ? error.message : "Failed"}
        </p>
      )}
      {data && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <StatCard
              label="Annual income"
              value={formatCentsCurrency(data.est_annual_income_cents)}
            />
            <StatCard
              label="Monthly avg"
              value={formatCentsCurrency(data.est_monthly_income_cents)}
            />
          </div>
          {data.upcoming.length === 0 ? (
            <p className="text-sm text-[var(--color-muted)]">
              No ex-div dates in the next {data.window_days} days.
            </p>
          ) : (
            <ul className="space-y-1.5">
              {data.upcoming.slice(0, 8).map((p, i) => (
                <li
                  key={`${p.ticker}-${p.ex_div_date}-${i}`}
                  className="flex items-center justify-between gap-2 text-sm"
                >
                  <div className="flex min-w-0 items-center gap-2">
                    <Pill tone="accent">{p.ex_div_date}</Pill>
                    <span className="font-mono text-[var(--color-text)]">
                      {p.ticker}
                    </span>
                  </div>
                  <span className="tabular-nums text-[var(--color-muted)]">
                    {formatCentsCurrency(p.est_payout_cents)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Card>
  );
}
